import {createContext, type PropsWithChildren, use, useCallback, useEffect, useState} from "react";
import messaging from "@react-native-firebase/messaging";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {AggregateActivity} from "@/lib/types";
import {fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";

const ActivityContext = createContext<{
    activities: AggregateActivity[];
    refreshActivities: () => any;
    clearActivities: () => any;
    activitiesBusy: boolean;
    activitiesLoading: boolean;
}>({
    activities: [],
    refreshActivities: () => {
    },
    clearActivities: () => {
    },
    activitiesBusy: false,
    activitiesLoading: true,
});

export function useActivity() {
    const value = use(ActivityContext);
    if (!value) {
        throw new Error('useNotifications must be wrapped in a <NotificationProvider />');
    }

    return value;
}

export function ActivityProvider({children}: PropsWithChildren) {
    const queryClient = useQueryClient();
    const {session} = useSession();

    const [activities, setActivities] = useState<AggregateActivity[]>([]);
    const [busy, setBusy] = useState(false);

    const refreshActivity = useCallback(async () => {
        setBusy(true);
        await queryClient.invalidateQueries({queryKey: ['account', 'activity', session]});
        setBusy(false);
    }, [queryClient, session]);

    const clearActivity = useCallback(async () => {
        try {
            setBusy(true);
            await fetchBackendNoContent("/account/activity", "DELETE", session);
            await queryClient.invalidateQueries({queryKey: ['account', 'activity', session]});
        } finally {
            setBusy(false);
        }
    }, [queryClient, session]);

    useEffect(() => {
        try {
            return messaging().onMessage(async _remoteMessage => {
                await refreshActivity();
            });
        } catch {
        }
    }, [queryClient, session, refreshActivity]);

    const {data, isLoading} = useQuery<AggregateActivity[]>({
        queryKey: ['account', 'activity', session],
        queryFn: () => fetchBackend(`/account/activity`, "GET", session),
        staleTime: 30000,
        retry: true,
        enabled: !!session,
    })

    useEffect(() => {
        if (data === undefined) {
            return;
        }
        setActivities(data);
    }, [data]);

    return (
        <ActivityContext.Provider
            value={{
                activities: activities,
                refreshActivities: refreshActivity,
                clearActivities: clearActivity,
                activitiesBusy: busy,
                activitiesLoading: isLoading,
            }}>
            {children}
        </ActivityContext.Provider>
    );
}